import { 
  Input,
  QueryList,
  Directive,
  HostBinding,
  ContentChildren,
} from '@angular/core';

/**
 * utils
 */
import { FromFieldUtils } from '@sharedModules/forms/utils/form-field-utils';

/**
 * enums
 */
import { FormFieldUI } from '@shared/modules/forms/enums/form-field-ui';
import { IFormFieldSizeType } from '@sharedModules/forms/enums/form-field-size';

/**
 * directives
 */
import { ElementTemplateDirective } from '@sharedDirectives/element-template/element-template.directive';

/**
 * @directive FormFieldGroupDirective
 * @description
 * **Angular directive for creating form field groups** that combine multiple form elements into a single cohesive unit.
 *
 * This directive provides a container for grouping form inputs, buttons, and text elements together,
 * creating complex form layouts with proper styling and behavior. It automatically handles the styling
 * of grouped elements and provides size variants, merge options, and rounded styling.
 *
 * **Key Features:**
 * - 🎯 **Input Group Container** - Creates Bootstrap-style input groups
 * - 🧩 **Element Template Integration** - Automatically styles group text elements
 * - 📏 **Size Variants** - Supports different field sizes (sm, md, lg)
 * - 🔗 **Merge Options** - Enables merged styling for adjacent elements
 * - 🎨 **Rounded Styling** - Optional pill-shaped rounded corners
 * - 🎪 **Automatic Styling** - Applies proper CSS classes to child elements
 *
 * **Common Use Cases:**
 * - Currency input with currency symbol prefix
 * - Search input with search button
 * - Email input with domain suffix
 * - File upload with browse button
 * - Password input with show/hide toggle
 *
 * @example
 * <!-- Basic input group -->
 * <div appFormFieldGroup>
 *   <span appElementTemplate="groupText">$</span>
 *   <input type="text" placeholder="Amount">
 * </div>
 *
 * @example
 * <!-- Input group with size and merge -->
 * <div appFormFieldGroup size="lg" [merge]="true">
 *   <input type="text" placeholder="Search...">
 *   <button appElementTemplate="groupText">Search</button>
 * </div>
 */
@Directive({
  selector: '[appFormFieldGroup]',
  host: {
    '[class.input-group]': 'true'
  }
})
export class FormFieldGroupDirective {

  /**
   * @property elementTemplates
   * @description
   * **Content children query for element templates** within the form field group.
   *
   * This property automatically queries for all `ElementTemplateDirective` instances
   * within the group and applies appropriate styling to group text elements.
   * When new elements are added or removed, the styling is automatically updated.
   *
   * @type {QueryList<ElementTemplateDirective> | null}
   * @example
   * <!-- Template with element templates -->
   * <div appFormFieldGroup>
   *   <span appElementTemplate="groupText">$</span>
   *   <input type="text">
   *   <button appElementTemplate="groupText">Submit</button>
   * </div>
   */
  @ContentChildren(ElementTemplateDirective)
  public set elementTemplates(list: QueryList<ElementTemplateDirective>) {

    // void
    if (list == null) return;

    // set _elementTemplates
    this._elementTemplates = list;

    this._handlerStyleGroupTextElements();
    
  }
  public get elementTemplates(): QueryList<ElementTemplateDirective> | null {
    return this._elementTemplates
  }
  private _elementTemplates!: QueryList<ElementTemplateDirective>

  /**
   * @property merge
   * @description
   * **Enables merged styling** for adjacent form elements within the group.
   *
   * When enabled, this property applies the `input-group-merge` CSS class,
   * which creates a seamless visual connection between adjacent form elements
   * by removing borders and spacing between them.
   *
   * @type {boolean}
   * @default false
   * @example
   * <!-- Merged input group -->
   * <div appFormFieldGroup [merge]="true">
   *   <input type="text" placeholder="Username">
   *   <input type="password" placeholder="Password">
   * </div>
   */
  @HostBinding('class.input-group-merge')
  @Input({ alias: 'merge' })
  public merge: boolean = false

  /**
   * @property rounded
   * @description
   * **Applies pill-shaped rounded corners** to the form field group.
   *
   * When enabled, this property applies the `rounded-pill` CSS class,
   * which creates fully rounded, pill-shaped corners for the entire group.
   * This is commonly used for search inputs and modern UI designs.
   *
   * @type {boolean}
   * @default false
   * @example
   * <!-- Rounded pill input group -->
   * <div appFormFieldGroup [rounded]="true">
   *   <span appElementTemplate="groupText">🔍</span>
   *   <input type="text" placeholder="Search...">
   * </div>
   */
  @HostBinding('class.rounded-pill')
  @Input({ alias: 'rounded' })
  public rounded : boolean = false

  /**
   * @property size
   * @description
   * **Controls the size variant** of the form field group.
   *
   * This property accepts `FormFieldSize` enum values or string keys and
   * automatically maps them to the appropriate CSS classes for consistent sizing.
   * The size affects all elements within the group for uniform appearance.
   *
   * @type {FormFieldSize}
   * @default FormFieldSize.Medium
   * @example
   * <!-- Large input group -->
   * <div appFormFieldGroup size="lg">
   *   <span appElementTemplate="groupText">$</span>
   *   <input type="text" placeholder="Amount">
   * </div>
   *
   * @example
   * <!-- Small input group -->
   * <div appFormFieldGroup size="sm">
   *   <input type="text" placeholder="Search">
   *   <button appElementTemplate="groupText">Go</button>
   * </div>
   */
  @Input({ alias: 'size' })
  public size!: IFormFieldSizeType


  /**
   * @property classStyleSize
   * @description
   * **Dynamic CSS class generator** for form field group sizing.
   *
   * This getter automatically generates the appropriate CSS classes based on the
   * current size setting and form field UI type. It uses the `FromFieldUtils.classStyleSize`
   * utility to ensure consistent styling across the application.
   *
   * @type {string}
   * @example
   * <!-- The directive automatically applies size classes -->
   * <div appFormFieldGroup size="lg">
   *   <!-- Generates: class="input-group-lg" -->
   * </div>
   */
  @HostBinding('class')
  public get classStyleSize() {
    return FromFieldUtils.classStyleSize(this.size, FormFieldUI.Group)
  }

  /**
   * @property groupTextElements
   * @description
   * **Filtered list of group text elements** within the form field group.
   *
   * This getter filters the element templates to return only those with the name 'groupText',
   * which are typically used for prefixes, suffixes, or action buttons within input groups.
   * These elements are automatically styled with the `input-group-text` CSS class.
   *
   * @type {ElementTemplateDirective[]}
   * @example
   * <!-- Group text elements are automatically styled -->
   * <div appFormFieldGroup>
   *   <span appElementTemplate="groupText">$</span>  <!-- Gets input-group-text class -->
   *   <input type="text">
   *   <button appElementTemplate="groupText">Submit</button>  <!-- Gets input-group-text class -->
   * </div>
   */
  public get groupTextElements(): ElementTemplateDirective[] {
    return this.elementTemplates instanceof QueryList ? this.elementTemplates.filter(x => x.name === 'groupText') : []
  }

  /**
   * @constructor
   * @description
   * **Initializes the form field group directive** with default settings.
   *
   * The constructor sets up the directive with default values for merge, rounded,
   * and size properties. No additional configuration is required for basic usage.
   */
  constructor() { }

  /**
   * @method _handlerStyleGroupTextElements
   * @description
   * **Private method to apply styling** to group text elements.
   *
   * This method iterates through all group text elements and applies the
   * `input-group-text` CSS class to ensure proper Bootstrap input group styling.
   * It's automatically called whenever the element templates are updated.
   *
   * @private
   * @example
   * // Called automatically when elementTemplates changes
   * this._handlerStyleGroupTextElements();
   */
  private _handlerStyleGroupTextElements(): void {

    // void
    if (this.groupTextElements.length === 0) return;

    // marked group text
    this.groupTextElements.forEach(tp => tp.htmlElement?.classList.add('input-group-text'))

  }

}
